import { z } from 'zod';
import { id, paginationQuery, paginatedResult, timestamp } from './common.js';
import { bookingStatus } from './shift.js';

export const supportCaseStatus = z.enum(['open', 'in_review', 'resolved', 'closed']);
export const supportCaseCategory = z.enum([
  'booking',
  'attendance',
  'hours',
  'payment',
  'cancellation',
  'practice',
  'other',
]);
export const supportActorSide = z.enum(['professional', 'office', 'support']);
export const supportVisibilityNotice =
  'This case and its updates are shared with the booked professional, authorized practice managers and organization support administrators. Do not include patient information or other private clinical details. This app does not provide emergency support or a guaranteed response time.';
export const supportBookingContext = z.object({
  bookingId: id,
  locationName: z.string(),
  professionalName: z.string(),
  startsAt: timestamp,
  endsAt: timestamp,
  timezone: z.string(),
  bookingStatus,
  callerSide: supportActorSide,
  canCreate: z.boolean(),
  visibilityNotice: z.string(),
});
export type SupportBookingContext = z.infer<typeof supportBookingContext>;
export const supportCase = z.object({
  id,
  reference: z.string(),
  bookingId: id,
  category: supportCaseCategory,
  subject: z.string(),
  description: z.string(),
  status: supportCaseStatus,
  revision: z.number().int().positive(),
  createdByUserId: id,
  createdByName: z.string(),
  createdAt: timestamp,
  updatedAt: timestamp,
  closedAt: timestamp.nullable(),
  resolution: z.string().nullable(),
  booking: supportBookingContext,
  canComment: z.boolean(),
  canEdit: z.boolean(),
  canStartReview: z.boolean(),
  canResolve: z.boolean(),
  canClose: z.boolean(),
  canReopen: z.boolean(),
});
export type SupportCase = z.infer<typeof supportCase>;
export const supportCaseEventKind = z.enum([
  'created',
  'updated',
  'comment',
  'start_review',
  'resolve',
  'close',
  'reopen',
]);
export const supportCaseEvent = z.object({
  id,
  caseId: id,
  kind: supportCaseEventKind,
  actorUserId: id,
  actorName: z.string(),
  actorSide: supportActorSide,
  body: z.string(),
  recordedAt: timestamp,
  revision: z.number().int().positive(),
});
export type SupportCaseEvent = z.infer<typeof supportCaseEvent>;
export const supportCasePage = paginatedResult(supportCase);
export const supportBookingPage = paginatedResult(supportBookingContext);
export const supportCaseDetail = z.object({
  case: supportCase,
  events: paginatedResult(supportCaseEvent),
  visibilityNotice: z.string(),
});
export type SupportCaseDetail = z.infer<typeof supportCaseDetail>;
export const supportCaseQuery = paginationQuery.extend({
  status: supportCaseStatus.optional(),
  category: supportCaseCategory.optional(),
  search: z.string().trim().max(100).optional(),
  bookingId: id.optional(),
});
export type SupportCaseQuery = z.infer<typeof supportCaseQuery>;
export const supportBookingQuery = paginationQuery.extend({
  search: z.string().trim().max(100).optional(),
});
export type SupportBookingQuery = z.infer<typeof supportBookingQuery>;
export const createSupportCaseInput = z
  .object({
    bookingId: id,
    category: supportCaseCategory,
    subject: z.string().trim().min(3).max(160),
    description: z.string().trim().min(10).max(5000),
    visibilityAcknowledged: z.literal(true),
    clientRequestId: z.uuid(),
  })
  .strict();
export type CreateSupportCaseInput = z.infer<typeof createSupportCaseInput>;
export const updateSupportCaseInput = z
  .object({
    category: supportCaseCategory,
    subject: z.string().trim().min(3).max(160),
    revision: z.number().int().positive(),
    clientRequestId: z.uuid(),
  })
  .strict();
export type UpdateSupportCaseInput = z.infer<typeof updateSupportCaseInput>;
export const supportCaseActionInput = z
  .object({
    action: z.enum(['comment', 'start_review', 'resolve', 'close', 'reopen']),
    body: z.string().trim().min(3).max(5000),
    revision: z.number().int().positive(),
    clientRequestId: z.uuid(),
  })
  .strict();
export type SupportCaseActionInput = z.infer<typeof supportCaseActionInput>;
